import { EmailLog } from "@/@types/EmailLog";
import BadgeShared from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EmailsCircle from "@/assets/icons/EmailsCircle";

interface EmailsListProps {
  emails: EmailLog[];
  onResend?: (emailId: string) => void;
  resendingEmailId?: string | null;
  className?: string;
  collapsible?: boolean;
}

const getStatusIcon = (status: string) => {
  const iconProps = { size: 14 };

  switch (status) {
    case "delivered":
      return <CheckCircle2 {...iconProps} className="text-[#A9D342]" />;
    case "failed":
      return <XCircle {...iconProps} className="text-[#F87171]" />;
    case "bounced":
      return <AlertTriangle {...iconProps} className="text-[#FB923C]" />;
    case "complained":
      return <ShieldAlert {...iconProps} className="text-[#DC2626]" />;
    case "pending":
      return <Clock {...iconProps} className="text-[#FACC15]" />;
    case "sent":
      return <Send {...iconProps} className="text-[#60A5FA]" />;
    default:
      return <Mail {...iconProps} className="text-[#A1A1AA]" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "delivered":
      return "bg-[#A9D342]";
    case "failed":
      return "bg-[#F87171]";
    case "bounced":
      return "bg-[#FB923C]";
    case "complained":
      return "bg-[#7F1D1D]";
    case "pending":
      return "bg-[#FACC15]";
    case "sent":
      return "bg-[#60A5FA]";
    default:
      return "bg-zinc-600";
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

const contentVariants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2 },
    },
  },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
};

export default function EmailsList({
  emails,
  onResend,
  resendingEmailId,
  className,
  collapsible = false,
}: EmailsListProps) {
  const { t } = useTranslation(["sales", "common"]);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!emails || emails.length === 0) {
    return null;
  }

  const deliveredCount = emails.filter((e) => e.status === "delivered").length;
  const failedCount = emails.filter((e) => e.status === "failed" || e.status === "bounced" || e.status === "complained").length;

  return (
    <Card className={cn("p-5 md:p-6", className)}>
      <div
        className={cn(
          "flex items-center justify-between",
          collapsible ? "cursor-pointer select-none" : "mb-5",
          collapsible && !isExpanded && "mb-0",
          collapsible && isExpanded && "mb-5"
        )}
        onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center gap-2">
          <EmailsCircle />
          <h2 className="text-colorText font-semibold text-base">
            {t("common:emails_section")}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {collapsible && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mr-1">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A9D342]" />
                {deliveredCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]" />
                {failedCount}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Mail size={12} className="text-[#60A5FA]" />
            <span>{emails.length}</span>
          </div>
          {collapsible && (
            <ChevronDown
              size={16}
              className={cn(
                "text-muted-foreground transition-transform duration-300",
                isExpanded && "rotate-180"
              )}
            />
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {(!collapsible || isExpanded) && (
          <motion.div
            initial={collapsible ? "collapsed" : false}
            animate="expanded"
            exit="collapsed"
            variants={contentVariants}
            className="overflow-hidden"
          >
            <motion.div
              className="space-y-2.5"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {emails.map((email) => {
                const isResending = resendingEmailId === email.id;

                return (
                  <motion.div key={email.id} variants={itemVariants}>
                    <div
                      className={cn(
                        "relative p-3.5 rounded-xl border border-zinc-800/80",
                        "bg-gradient-to-br from-zinc-900/50 to-zinc-950/50",
                        "group transition-all duration-200",
                        "hover:border-zinc-700/80 hover:shadow-lg hover:shadow-black/20",
                        isResending && "opacity-70 pointer-events-none"
                      )}
                    >
                      <div
                        className={cn(
                          "absolute left-0 top-3 bottom-3 w-0.5 rounded-full transition-all",
                          getStatusColor(email.status)
                        )}
                      />

                      <div className="flex items-start justify-between gap-3 pl-2">
                        <div className="flex flex-col gap-2 min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(email.status)}
                            <span className="text-sm font-medium text-colorText truncate group-hover:text-white transition-colors">
                              {email.type_label}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <BadgeShared status={email.status} />

                            {email.attempts > 1 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                      <RefreshCw size={10} />
                                      {email.attempts}/3
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      {t("common:attempts_count", {
                                        current: email.attempts,
                                        max: 3,
                                      })}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>

                          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground/70">
                            <span className="flex items-center gap-1">
                              <Mail size={10} />
                              {email.email_sent}
                            </span>

                            {email.sent_at && (
                              <span className="flex items-center gap-1">
                                <Clock size={10} />
                                {new Date(email.sent_at).toLocaleString()}
                              </span>
                            )}
                          </div>

                          {email.error_message && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs text-[#F87171] truncate max-w-[250px] cursor-help">
                                    {email.error_message}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[300px]">
                                  <p>{email.error_message}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          {email.can_resend && onResend && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                      "h-8 w-8 rounded-lg",
                                      "text-muted-foreground hover:text-[#60A5FA]",
                                      "hover:bg-[#60A5FA]/10",
                                      "transition-all duration-200"
                                    )}
                                    onClick={() => onResend(email.id)}
                                    disabled={isResending}
                                  >
                                    {isResending ? (
                                      <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                      <RefreshCw size={14} />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t("common:resend_email")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {!collapsible && (
              <div className="mt-4 pt-4 border-t border-zinc-800/50">
                <div className="flex items-center flex-wrap gap-2 sm:gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A9D342]" />
                    {deliveredCount} {t("common:delivered")}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]" />
                    {failedCount} {t("common:failed")}
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
